import { useMemo } from "react";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";
import type { ChildPage, Page } from "../../shared/schema";
import type { DataSourceType, VisibilityCondition } from "@tellescope/visibility-schema";

export interface UseVisiblePagesResult {
  /** Top-level pages, each with its `pages` (sub-pages) filtered down to visible ones */
  visiblePages: Page[];
  isLoading: boolean;
}

// Sub-page visibility is independent of its parent's visibility - a hidden parent
// does not hide its children (they're still reachable directly by URL).
export const useVisiblePages = (pages: Page[]): UseVisiblePagesResult => {
  const requiredSources = useMemo(() => {
    const sources = new Set<DataSourceType>();
    const collect = (page: Page | ChildPage) => {
      if (page.visibility?.condition) {
        extractDataSources(page.visibility.condition).forEach((s) => sources.add(s));
      }
    };
    for (const page of pages) {
      collect(page);
      (page.pages ?? []).forEach(collect);
    }
    return sources;
  }, [pages]);

  const { context, isLoading } = useVisibilityContext({ requiredSources });

  const isVisible = useMemo(() => {
    return (condition: VisibilityCondition | undefined) => {
      if (!condition) return true;
      // Show while loading to prevent navigation from collapsing
      if (isLoading || !context) return true;
      return evaluateVisibility(condition, context);
    };
  }, [context, isLoading]);

  const visiblePages = useMemo(() => {
    const result: Page[] = [];
    for (const page of pages) {
      if (!isVisible(page.visibility?.condition)) continue;
      result.push({
        ...page,
        pages: (page.pages ?? []).filter((child) => isVisible(child.visibility?.condition)),
      });
    }
    return result;
  }, [pages, isVisible]);

  return { visiblePages, isLoading };
};
